import {
  ACDL_CONSENT_UPDATED,
  ACDL_FPID_CACHE_HIT,
  ACDL_FPID_FETCH_FAILED,
  ACDL_FPID_FETCH_START,
  ACDL_FPID_FETCH_SUCCESS,
  ACDL_PRE_HIDING_DEACTIVATED,
} from '@constants/events';

const FPID_ENDPOINT = '/bin/aep/fpid';
const FPID_SESSION_KEY = 'aep.fpid.session';
// Versioned so deploying a server-side FPID cookie fix forces one re-fetch for visitors who
// already hold a sentinel from before the fix — see LEGACY_FPID_PRESENCE_COOKIES below.
const FPID_PRESENCE_COOKIE = 'fpid.present.v2';
// Sentinel names retired by previous versions of this cookie-presence check. Cleared opportunistically
// on the next successful fetch so they don't linger in the browser once superseded.
const LEGACY_FPID_PRESENCE_COOKIES = ['fpid.present'];
// 13 months — matches the server-side HttpOnly FPID cookie lifetime issued by ACS Commons.
const FPID_COOKIE_MAX_AGE = 33_696_000;
const FPID_STRATEGY_SENTINEL = 'sentinel';
const FPID_STRATEGY_SESSION = 'session';

/**
 * DataLayerService — singleton that manages the Adobe Client Data Layer (ACDL).
 *
 * Responsibilities:
 * - Bridges OneTrust consent with Adobe Data Collection script loading.
 *   Listens for `OneTrustGroupsUpdated` events and activates/deactivates the
 *   Data Collection script based on the user's consent state. When consent is
 *   revoked after the script was already loaded, the page is reloaded.
 * - Manages the Adobe Target pre-hiding snippet (`alloy-prehiding`).
 *   Removes it when consent is missing and registers Alloy monitoring to track
 *   pre-hiding state changes when consent is granted.
 * - Pushes consent state (`user.consent.experience_cloud`) into the ACDL so
 *   downstream rules can react to it. When the AEM configuration overrides
 *   consent (`ignoreConsent`), the state is set to `'override'`.
 * - Seeds the AEP first-party identifier (FPID) HttpOnly cookie via
 *   `/bin/aep/fpid` before the Data Collection script activates. The cookie
 *   itself is the only identity carrier — Adobe Edge resolves FPID → ECID
 *   server-side via the first-party CNAMEd Edge endpoint, so the JS never
 *   reads or transmits the UUID. ACDL push is observability only (trace
 *   events; no `identityMap`). See {@link DataLayerService.publishFpid}.
 *
 * Usage from components:
 *
 *   import dataLayerService from '@services/DataLayerService';
 *
 *   // Push a component interaction event
 *   dataLayerService.push({
 *     eventName: 'cta:click',
 *     type: 'component',
 *     id: 'my-component-id',
 *     data: { action: 'download', label: 'Brochure' },
 *   });
 *
 *   // Push a page-level update (uses the page ID from <body id="...">)
 *   dataLayerService.push({
 *     eventName: 'virtual-pageview',
 *     type: 'page',
 *     id: dataLayerService.pageID,  // not directly accessible — see note below
 *     data: { section: 'configurator' },
 *   });
 *
 *   // Read the current ACDL state (resolves via ACDL getState or fallback merge)
 *   const state = await dataLayerService.getState();
 *
 * The `push()` method automatically:
 * - Nests `data` under `{type}.{id}` in the ACDL (e.g. `component.my-component-id`)
 * - Adds a timestamp to the data
 * - Sets `eventInfo.reference` for ACDL event-component binding
 */
export interface DataLayerDetails {
  timestamp: number;
  [ind: string]: unknown;
}

export type DataLayerEvent = {
  eventName?: string;
  type: 'component' | 'page' | 'user' | undefined;
  id: string;
  data: Record<string, unknown>;
};

export class DataLayerService {
  private pageID;
  private alloyMonitoringRegistered = false;

  constructor() {
    this.pageID = this.set_page_id();
    this.handleConsentChanges();
  }

  async getState(): Promise<unknown> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        const fallbackState = this.mergeDataLayerArrayFallback();
        resolve(fallbackState);
      }, 500);

      window.adobeDataLayer = window.adobeDataLayer || [];
      window.adobeDataLayer.push(function (dl: { getState: () => unknown }) {
        clearTimeout(timeout);
        const state = dl.getState();
        resolve(state);
      });
    });
  }

  push(dataLayerEvent: DataLayerEvent): void {
    const event = dataLayerEvent.eventName;
    const eventInfo = this.buildEventInfo(dataLayerEvent.type, dataLayerEvent.id);
    const update = this.nester(eventInfo?.eventInfo?.reference, this.prepareTrackingEvent(dataLayerEvent.data));

    const pushObject: Record<string, unknown> = {
      ...(event && { event }),
      ...eventInfo,
      ...update,
    };

    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(pushObject);
  }

  private set_page_id() {
    const page_id = document.body?.id;
    if (page_id) return page_id;
    console.error('Missing page-id on body tag!');
    return '';
  }

  private buildEventInfo(
    type: string | undefined,
    id: string | undefined
  ): { eventInfo: { reference: string } } | undefined {
    if (!type || !id) return undefined;
    const reference = `${type}.${id}`;
    return { eventInfo: { reference } };
  }

  private prepareTrackingEvent(eventDetails: Record<string, unknown> | undefined): DataLayerDetails | undefined {
    if (!eventDetails) return undefined;
    return {
      timestamp: new Date().getTime(),
      ...eventDetails,
    };
  }

  private nester(reference: string | undefined, data: unknown) {
    if (!reference) return undefined;
    const obj: Record<string, unknown> = {};
    reference.split('.').reduce<Record<string, unknown>>(function (acc, item, index, arr) {
      if (index === arr.length - 1) {
        acc[item] = data;
        return obj;
      }
      const next = {};
      acc[item] = next;
      return next;
    }, obj);
    return obj;
  }

  private handleConsentChanges() {
    const boundHandleScriptLoading = this.handleScriptLoading.bind(this);
    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(function (dl: { addEventListener: (event: string, fn: unknown) => unknown }) {
      dl.addEventListener('OneTrustGroupsUpdated', boundHandleScriptLoading);
    });
  }

  private mergeDataLayerArrayFallback(): unknown {
    return (window.adobeDataLayer || [])
      .filter((item: Record<string, unknown>) => typeof item === 'object' && item !== null)
      .reduce((acc: Record<string, unknown>, item: Record<string, unknown>) => {
        return { ...acc, ...item };
      }, {});
  }

  private async handleScriptLoading(event: { event: string; OnetrustActiveGroups: string }) {
    const scripts = document.querySelectorAll('script[data-consent-category]');
    for (const script of scripts) {
      const category = script.getAttribute('data-consent-category');
      if (!category) {
        this.push({
          eventName: ACDL_CONSENT_UPDATED,
          type: 'user',
          id: 'consent.experience_cloud',
          data: { state: 'override' },
        });
        continue;
      }
      const has_consent = event.OnetrustActiveGroups?.includes(category);
      this.push({
        eventName: ACDL_CONSENT_UPDATED,
        type: 'user',
        id: 'consent.experience_cloud',
        data: { state: !!has_consent },
      });
      if (has_consent && (script as HTMLScriptElement).type === 'text/plain') {
        // Seed the FPID HttpOnly cookie before activating the Data Collection script,
        // so the very first Edge call from Alloy already carries the cookie and the
        // resulting ECID is linked to FPID server-side from request #1.
        const strategy = script.getAttribute('data-fpid-strategy') ?? FPID_STRATEGY_SENTINEL;
        await this.publishFpid(strategy);
        const clonedScript = script.cloneNode(true) as HTMLScriptElement;
        clonedScript.type = 'text/javascript';
        script.parentNode?.replaceChild(clonedScript, script);
        this.registerAlloyMonitoring();
      }

      if (!has_consent) {
        this.removePreHidingSnippet();
        if ((script as HTMLScriptElement).type === 'text/javascript') {
          window.location.reload();
        }
      }
    }
  }

  /**
   * Ensures the AEP first-party identifier HttpOnly cookie is seeded on the
   * device, so Adobe Edge can resolve FPID → ECID server-side on subsequent
   * Edge calls (which travel over the first-party CNAMEd Edge endpoint and
   * therefore carry the cookie automatically).
   *
   * Endpoint `GET /bin/aep/fpid` is served by ACS Commons' FpidCookieServlet
   * (`com.adobe.acs.commons.aep.impl.FpidCookieServlet`, shipped in acs-aem-commons).
   * It sets a single HttpOnly `FPID` cookie (13-month TTL, Secure, SameSite=Lax).
   * Do not reimplement — ACS Commons already registers a servlet at this path;
   * a custom one would be shadowed at runtime.
   *
   * Why we do not read the UUID client-side: Adobe Edge resolves FPID → ECID
   * via the cookie that travels with every Edge request (CNAME setup makes the
   * Edge endpoint first-party). Sending the UUID a second time via `identityMap`
   * would be redundant and creates a drift failure mode if cookie and any JS
   * cache disagree. The cookie is the single source of truth.
   *
   * Presence detection: `FPID` is HttpOnly, so JS cannot read it. To avoid a
   * fetch on every page view we write a separate non-HttpOnly presence sentinel
   * cookie (`FPID_PRESENCE_COOKIE`) after a successful fetch. Sentinel present ⇒
   * treat the server cookie as still alive, skip the fetch. Browser "Clear site
   * data" wipes the HttpOnly FPID and the sentinel together, so the next page
   * falls through to a fresh fetch and re-syncs. The sentinel name is versioned
   * (`LEGACY_FPID_PRESENCE_COOKIES`) so a server-side FPID cookie fix can force
   * one re-fetch for visitors who already hold a sentinel from before the fix —
   * without it, they'd never call this endpoint again and never receive it.
   *
   * Cache busting: a unique `?_=…` query param plus `cache: 'no-store'` plus a
   * `Cache-Control: no-store` request header force dispatcher / CDN / proxies to
   * bypass their caches — required by Adobe's spec because the response sets a
   * per-user cookie and a cached reply would leak one user's FPID to the next.
   *
   * `strategy` is read from the Data Collection script's `data-fpid-strategy`
   * attribute (rendered in HTL from CAConfig `DataCollectionConfig.useSessionStrategy`):
   * - `"sentinel"` (default): fetch only when the sentinel cookie is missing.
   * - `"session"`: additionally fetch once per browser tab session (via a
   *   sessionStorage marker) regardless of sentinel state. Costs ~1 extra fetch
   *   per session as a safety net against stale identity (e.g. user manually
   *   clearing only the HttpOnly FPID).
   * Unknown values fall back to sentinel behavior.
   *
   * Observability: emits ACDL trace events (`fpid:cache-hit`, `fpid:fetch-start`,
   * `fpid:fetch-success`, `fpid:fetch-failed`) so the client-side flow can be
   * inspected in DevTools / ACDL state. No identity values are pushed.
   */
  private async publishFpid(strategy: string): Promise<void> {
    const hasSentinel = this.hasFpidPresenceSentinel();
    const isSessionStrategy = strategy === FPID_STRATEGY_SESSION;
    const needSessionRefresh = isSessionStrategy && !this.hasFpidSessionMarker();
    const needFetch = !hasSentinel || needSessionRefresh;

    if (!needFetch) {
      this.pushFpidTrace(ACDL_FPID_CACHE_HIT, { strategy });
      return;
    }

    const reason = !hasSentinel ? 'no_sentinel' : 'session_marker_missing';
    this.pushFpidTrace(ACDL_FPID_FETCH_START, { strategy, reason });

    const result = await this.fetchFpid();
    if (result.ok) {
      this.setFpidPresenceSentinel();
      if (isSessionStrategy) this.setFpidSessionMarker();
      this.pushFpidTrace(ACDL_FPID_FETCH_SUCCESS, { strategy });
    } else {
      this.pushFpidTrace(ACDL_FPID_FETCH_FAILED, { strategy, status: result.status });
    }
  }

  private pushFpidTrace(eventName: string, data: Record<string, unknown>): void {
    this.push({ eventName, type: 'user', id: 'fpid', data });
  }

  private hasFpidSessionMarker(): boolean {
    try {
      return window.sessionStorage?.getItem(FPID_SESSION_KEY) === '1';
    } catch {
      return false;
    }
  }

  private setFpidSessionMarker(): void {
    try {
      window.sessionStorage?.setItem(FPID_SESSION_KEY, '1');
    } catch {
      // storage denied (private browsing, quota, etc.) — proceed without session marker
    }
  }

  private async fetchFpid(): Promise<{ ok: boolean; status?: number }> {
    try {
      // Query-param cache buster per Adobe's reference: forces dispatcher/CDN/proxies
      // to pass through regardless of their own cache rules. The HttpOnly FPID cookie
      // is set via the response Set-Cookie header; we do not consume the response body.
      const url = `${FPID_ENDPOINT}?_=${Date.now()}${Math.random()}`;
      const res = await fetch(url, {
        credentials: 'include',
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-store' },
      });
      if (!res.ok) {
        console.error(`FPID fetch failed: HTTP ${res.status}`);
        return { ok: false, status: res.status };
      }
      return { ok: true, status: res.status };
    } catch (e) {
      console.error('FPID fetch error', e);
      return { ok: false };
    }
  }

  private hasFpidPresenceSentinel(): boolean {
    return document.cookie.split('; ').some((c) => c.startsWith(`${FPID_PRESENCE_COOKIE}=`));
  }

  private setFpidPresenceSentinel(): void {
    // Value is irrelevant — only the presence of this cookie is consulted. Lifetime matches
    // the server FPID cookie so they expire together. Secure: browsers treat http://localhost
    // as a secure context, so this works in local dev too.
    document.cookie = `${FPID_PRESENCE_COOKIE}=1; path=/; max-age=${FPID_COOKIE_MAX_AGE}; Secure; SameSite=Lax`;
    LEGACY_FPID_PRESENCE_COOKIES.forEach((name) => {
      document.cookie = `${name}=; path=/; max-age=0; Secure; SameSite=Lax`;
    });
  }

  private removePreHidingSnippet() {
    const preHidingStyle = document.getElementById('alloy-prehiding');
    if (!preHidingStyle) return;
    preHidingStyle.parentNode?.removeChild(preHidingStyle);
    setTimeout(() => {
      this.push({
        eventName: ACDL_PRE_HIDING_DEACTIVATED,
        id: this.pageID,
        type: 'page',
        data: {
          preHidingState: {
            active: false,
            reason: 'Missing consent at page load time',
          },
        },
      });
    }, 0);
  }

  private registerAlloyMonitoring() {
    if (this.alloyMonitoringRegistered) return;
    this.alloyMonitoringRegistered = true;

    const bound_push = this.push.bind(this);
    const pageID = this.pageID;

    const win = window as unknown as Record<string, unknown[]>;
    win.__alloyMonitors ??= [];
    win.__alloyMonitors.push({
      onContentHiding: (data: { status: string }) => {
        bound_push({
          eventName: ACDL_PRE_HIDING_DEACTIVATED,
          id: pageID,
          type: 'page',
          data: {
            preHidingState: {
              active: data.status !== 'show-containers',
              reason: `Alloy: ${data.status}`,
            },
          },
        });
      },
    });
  }
}

export default new DataLayerService();
